// Portal sections (analysisItems.category) and what the call review offers in each. One review screen for all of them
// (user, 7 Oct 2026: reuse the humpback tools for baleen whales, dolphin whistles and the rest).
// Species -> section follows the engine (cetalingua_engine/routing.py): 1-4 manatee, 11 humpback, 5-10 + 12-18 baleen,
// 19-21 sperm whales, 22-85 toothed whales / dolphins / porpoises split by sound (whistles vs clicks & bursts).
import SPECIES from './species.json';

export const NONLINEAR = {
  'frequency jump': { color: '#ff6b6b', name: 'Frequency jump' },
  subharmonics: { color: '#c77dff', name: 'Subharmonics' },
  biphonation: { color: '#ffd43b', name: 'Biphonation' },
  chaos: { color: '#ff9f1c', name: 'Chaos' },
  sidebands: { color: '#f783ac', name: 'Sidebands' },
  'possible aliasing': { color: '#4dd4ff', name: 'Possible aliasing' },
};
export const PULSE_BOXES = {
  'click train': { color: '#ff9f1c', name: 'Click train' },
  'burst pulse': { color: '#ff6b6b', name: 'Burst pulse' },
  buzz: { color: '#c77dff', name: 'Buzz' },
  coda: { color: '#ffd43b', name: 'Coda' },
  creak: { color: '#f783ac', name: 'Creak' },
  'slow click': { color: '#69db7c', name: 'Slow click' },
};
// 'missed call' (13 Oct 2026, engine feedback loop): another call in the frame that the engine did not box — the
// engine's recall is measured on these (cetalingua_engine/feedback.py)
export const OTHER_BOXES = {
  'other sound': { color: '#adb5bd', name: 'Other sound (not this call)' },
  'missed call': { color: '#ffffff', name: 'Missed call (the engine did not box it)' },
};
export const LINE_KINDS = {
  f0: { color: '#00e5ff', name: 'Fundamental (F0)', width: 2.4 },
  harmonic: { color: '#9fd8ff', name: 'Harmonic', width: 1.4 },
  subharmonic: { color: '#c77dff', name: 'Subharmonic', width: 1.4 },
};
export const PART = { T: ['#3fb950', 'tonal'], Tc: ['#f0883e', 'tonal + noise'], X: ['#f85149', 'noisy'], P: ['#d29922', 'pulsed'], U: ['#6e7681', 'faint'] };

const pick = (o, keys) => Object.fromEntries(keys.map((k) => [k, o[k]]));
const TONAL_TOOLS = ['select', 'f0', 'harmonic', 'subharmonic', 'box'];
const PULSED_TOOLS = ['select', 'mark', 'box'];

// measurement labels shared by all sections: [key, label, unit]
export const LABELS = {
  duration_s: ['Duration', 's'], n_parts: ['Parts', ''], parts_seq: ['Parts in order', ''], snr_db: ['Signal over noise', 'dB'],
  low_hz: ['Lowest', 'Hz'], high_hz: ['Highest', 'Hz'], bandwidth_hz: ['Bandwidth', 'Hz'], peak_hz: ['Peak', 'Hz'],
  bw10_hz: ['Peak −10 dB bandwidth', 'Hz'], q1_hz: ['Energy quartile 25 %', 'Hz'], q2_hz: ['Energy quartile 50 %', 'Hz'], q3_hz: ['Energy quartile 75 %', 'Hz'],
  f0_start: ['F0 start', 'Hz'], f0_end: ['F0 end', 'Hz'], f0_min: ['F0 min', 'Hz'], f0_median: ['F0 median', 'Hz'], f0_max: ['F0 max', 'Hz'],
  f0_excursion_hz: ['F0 excursion', 'Hz'], contour: ['Contour', ''], harmonic_bands: ['Harmonic bands', ''], harmonic_spacing_hz: ['Harmonic spacing', 'Hz'],
  tonal_share: ['Tonal share', '0–1'], noisy_share: ['Noisy share', '0–1'], pulsed_share: ['Pulsed share', '0–1'], pulse_rate_hz: ['Pulse rate', '/s'],
  am_depth_db: ['Level variation (AM)', 'dB'],
  f_start_hz: ['Start frequency', 'Hz'], f_end_hz: ['End frequency', 'Hz'], f_min_hz: ['Lowest frequency', 'Hz'], f_max_hz: ['Highest frequency', 'Hz'],
  sweep_rate_hz_s: ['Sweep rate', 'Hz/s'], steps: ['Steps', ''], harmonics: ['Harmonics', ''], awslope: ['Slope (LFDCS)', ''],
  species_margin: ['Species margin', ''], f0_start_hz: ['F0 start', 'Hz'], f0_end_hz: ['F0 end', 'Hz'], f0_min_hz: ['F0 min', 'Hz'],
  f0_max_hz: ['F0 max', 'Hz'], f0_range_hz: ['F0 range', 'Hz'], harmonics_traced: ['Harmonics traced', ''], dominant_harmonic: ['Loudest harmonic', ''],
  trains: ['Click trains', ''], bursts: ['Burst pulses', ''], clicks: ['Clicks', ''], ici_median_ms: ['Inter-click interval (median)', 'ms'],
  peak_freq_hz: ['Peak frequency', 'Hz'], rhythm: ['Rhythm', ''],
};
const HUMPBACK_GROUPS = [
  ['Call', ['duration_s', 'n_parts', 'parts_seq', 'snr_db']],
  ['Frequency', ['low_hz', 'high_hz', 'bandwidth_hz', 'peak_hz', 'bw10_hz', 'q1_hz', 'q2_hz', 'q3_hz']],
  ['Fundamental (F0)', ['f0_start', 'f0_end', 'f0_min', 'f0_median', 'f0_max', 'f0_excursion_hz', 'contour']],
  ['Harmonics & structure', ['harmonic_bands', 'harmonic_spacing_hz', 'tonal_share', 'noisy_share', 'pulsed_share', 'pulse_rate_hz', 'am_depth_db']],
];

// f0: which measurements follow the drawn F0 line; call: which follow the whole-call box
export const SECTIONS = [
  { key: 'humpback', group: 'Tonal', label: 'Humpback', signal: 'tonal', codeSystem: 'WC', finder: 'WC', tools: TONAL_TOOLS,
    boxes: pick(NONLINEAR, ['frequency jump', 'subharmonics', 'biphonation', 'chaos', 'sidebands', 'possible aliasing']), partsBar: true,
    autoHarmonics: true, groups: HUMPBACK_GROUPS,
    broad: ['low harmonic (F0 < 600 Hz)', 'mid harmonic (600-1600 Hz)', 'high harmonic (> 1600 Hz)', 'two-part / composite', 'noisy / broadband', 'amplitude-modulated', 'pulsed'],
    f0: { start: 'f0_start', end: 'f0_end', min: 'f0_min', max: 'f0_max', median: 'f0_median', range: 'f0_excursion_hz' },
    call: { duration: 'duration_s', low: 'low_hz', high: 'high_hz', bandwidth: 'bandwidth_hz' } },
  { key: 'baleen', group: 'Tonal', label: 'Baleen whales', signal: 'tonal', codeSystem: 'PAM', finder: 'PAM', tools: TONAL_TOOLS,
    boxes: pick(NONLINEAR, ['frequency jump', 'subharmonics', 'biphonation', 'chaos', 'sidebands', 'possible aliasing']),
    f0: { start: 'f_start_hz', end: 'f_end_hz', min: 'f_min_hz', max: 'f_max_hz' }, call: { duration: 'duration_s' } },
  { key: 'delphinid_tonal', group: 'Tonal', label: 'Dolphin whistles', signal: 'tonal', codeSystem: 'DC', codePrefix: 'T_', tools: TONAL_TOOLS,
    boxes: pick(NONLINEAR, ['frequency jump', 'subharmonics', 'biphonation', 'chaos', 'sidebands', 'possible aliasing']),
    f0: { start: 'f_start_hz', end: 'f_end_hz', min: 'f_min_hz', max: 'f_max_hz', range: 'bandwidth_hz' }, call: { duration: 'duration_s' } },
  { key: 'manatee', group: 'Tonal', label: 'Manatees', signal: 'tonal', codeSystem: 'MC', tools: TONAL_TOOLS,
    boxes: pick(NONLINEAR, ['frequency jump', 'subharmonics', 'biphonation', 'chaos', 'sidebands', 'possible aliasing']),
    f0: { start: 'f0_start_hz', end: 'f0_end_hz', min: 'f0_min_hz', max: 'f0_max_hz', range: 'f0_range_hz' }, call: { duration: 'duration_s' } },
  { key: 'delphinid_pulsed', group: 'Pulsed', label: 'Dolphins & blackfish', long: 'Dolphins and blackfish — clicks & burst pulses', signal: 'pulsed',
    codeSystem: 'DC', codePrefix: 'P_', tools: PULSED_TOOLS, boxes: pick(PULSE_BOXES, ['click train', 'burst pulse', 'buzz']) },
  { key: 'beaked_whale', group: 'Pulsed', label: 'Beaked whales', long: 'Beaked whales — echolocation clicks and buzzes', signal: 'pulsed',
    codeSystem: 'DC', codePrefix: 'P_', tools: PULSED_TOOLS, boxes: pick(PULSE_BOXES, ['click train', 'buzz']) },
  { key: 'porpoise_nbhf', group: 'Pulsed', label: 'Porpoises & NBHF', long: 'Porpoises and other narrow-band high-frequency species — clicks', signal: 'pulsed',
    codeSystem: 'DC', codePrefix: 'P_', tools: PULSED_TOOLS, boxes: pick(PULSE_BOXES, ['click train', 'buzz']) },
  { key: 'sperm_whale', group: 'Pulsed', label: 'Sperm whales', long: 'Sperm whales — usual clicks, creaks, codas', signal: 'pulsed', codeSystem: 'PAM',
    codeFilter: (c) => c.startsWith('SpW'), tools: PULSED_TOOLS, boxes: pick(PULSE_BOXES, ['click train', 'coda', 'creak', 'slow click']) },
  { key: 'unsorted', group: 'Other', label: 'Unsorted', signal: 'any', codeSystem: null, tools: [...TONAL_TOOLS, 'mark'], needsSpecies: true,
    boxes: { ...pick(NONLINEAR, ['frequency jump', 'subharmonics', 'biphonation', 'chaos']), ...pick(PULSE_BOXES, ['click train', 'burst pulse']) } },
].map((s) => ({ ...s, boxes: { ...s.boxes, ...OTHER_BOXES } }));
export const SECTION = Object.fromEntries(SECTIONS.map((s) => [s.key, s]));

// the engine's acoustic profiles (reference/species_profiles.json): beaked_whale and nbhf species
const BEAKED = new Set([24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42]);
const NBHF = new Set([44, 47, 48, 49, 50, 68, 69, 80, 81, 82, 83, 84, 85]);
// species id -> section (same as routing.py category_for_species); signal = 'tonal' | 'pulsed'
export function sectionForSpecies(id, signal) {
  const s = Number(id);
  if (!id && id !== 0) return null;
  if (s >= 1 && s <= 4) return 'manatee';
  if (s === 11) return 'humpback';
  if ((s >= 5 && s <= 10) || (s >= 12 && s <= 18)) return 'baleen';
  if (s >= 19 && s <= 21) return 'sperm_whale';
  if (s >= 22 && s <= 85) {
    if (signal !== 'pulsed') return 'delphinid_tonal';
    if (BEAKED.has(s)) return 'beaked_whale';
    if (NBHF.has(s)) return 'porpoise_nbhf';
    return 'delphinid_pulsed';
  }
  return null;
}
export const SPECIES_LIST = Object.entries(SPECIES).map(([id, name]) => ({ id: Number(id), name })).filter((x) => x.id <= 85);
export const speciesName = (id) => (id ? SPECIES[String(id)] || null : null);
